import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@store/authStore';
import { useToast } from '@hooks/use-toast';

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  skipErrorToast?: boolean;
}

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? '/api/v1',
  timeout: Number(import.meta.env.VITE_API_TIMEOUT) || 30_000,
  headers: { 'Content-Type': 'application/json' },
});

// Attach JWT to every request
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // When sending FormData, remove Content-Type so browser sets multipart/form-data with boundary
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
});

let refreshRequest: Promise<string> | null = null;

async function getFreshAccessToken(): Promise<string> {
  if (refreshRequest) return refreshRequest;

  const refreshToken = useAuthStore.getState().refreshToken;
  if (!refreshToken) throw new Error('No refresh token available');

  const rawBaseURL = api.defaults.baseURL ?? '/api/v1';
  const baseURL = rawBaseURL.replace(/\/+$/, '');
  const refreshUrl = `${baseURL}/auth/refresh`;

  refreshRequest = axios
    .post(refreshUrl, { refreshToken })
    .then(({ data }) => {
      const accessToken = data?.data?.accessToken as string;
      if (!accessToken) throw new Error('Failed to retrieve access token from refresh response');
      useAuthStore.getState().setAccessToken(accessToken);
      return accessToken;
    })
    .finally(() => {
      refreshRequest = null;
    });

  return refreshRequest;
}

// Refresh an expired access token once, then retry the original request.
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryableRequestConfig | undefined;
    const requestUrl = originalRequest?.url ?? '';
    const isAuthRequest =
      requestUrl.includes('/auth/login') ||
      requestUrl.includes('/auth/refresh') ||
      requestUrl.includes('/auth/forgot-password') ||
      requestUrl.includes('/auth/reset-password');

    const isToastSkipped =
      Boolean(
        originalRequest?.skipErrorToast ||
        (originalRequest?.params as Record<string, unknown> | undefined)?.skipToast ||
        originalRequest?.headers?.['x-skip-toast']
      );

    if (error.response?.status === 403 && !isToastSkipped) {
      useToast.getState().toast({
        type: 'error',
        message: 'Access Denied: you do not have permission to perform this action.',
      });
    }

    if (error.response?.status === 401 && !isAuthRequest) {
      if (originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          const accessToken = await getFreshAccessToken();
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return api(originalRequest);
        } catch {
          useAuthStore.getState().logout();
          if (window.location.pathname !== '/login') {
            window.location.assign('/login');
          }
          return Promise.reject(error);
        }
      }

      // If token refresh failed, or already retried once and still unauthorized
      useAuthStore.getState().logout();
      if (window.location.pathname !== '/login') {
        window.location.assign('/login');
      }
    }

    if (error.code === 'ECONNABORTED' || error.message?.toLowerCase().includes('timeout')) {
      error.message = 'Request timed out. The server is taking longer than expected to respond. Please try again.';
      if (error.response?.data && typeof error.response.data === 'object') {
        (error.response.data as any).message = error.message;
        (error.response.data as any).error = error.message;
      }
    }

    return Promise.reject(error);
  }
);

// ΓöÇΓöÇΓöÇ Helpers ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
export const getAuthImageUrl = (url?: string | null) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  const token = useAuthStore.getState().accessToken;
  if (!token) return url;
  return url.includes('?') ? `${url}&token=${token}` : `${url}?token=${token}`;
};

// ΓöÇΓöÇΓöÇ Auth ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
export const authApi = {
  login: (data: { email: string; password: string; recaptchaToken?: string }) =>
    api.post('/auth/login', data),
  logout: (refreshToken?: string) =>
    api.post('/auth/logout', refreshToken ? { refreshToken } : {}),
  me: () => api.get('/auth/me'),
  refreshToken: (refreshToken: string) => api.post('/auth/refresh', { refreshToken }),
  forgotPassword: (email: string) => api.post('/auth/forgot-password', { email }),
  resetPassword: (data: { token: string; password: string }) =>
    api.post('/auth/reset-password', data),
  changePassword: (data: { currentPassword?: string; newPassword: string }) =>
    api.post('/auth/change-password', data),
};

// ΓöÇΓöÇΓöÇ Notifications ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
export interface NotificationRecord {
  id: string;
  tenantId: string;
  userId: string;
  type: string;
  title: string;
  body: string;
  isRead: boolean;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export interface NotificationListParams {
  page?: number;
  limit?: number;
  unreadOnly?: boolean;
}

export const notificationsApi = {
  list: (params?: NotificationListParams) =>
    api.get<{ success: boolean; data: NotificationRecord[] }>('/notifications', { params }),
  unreadCount: () =>
    api.get<{ success: boolean; data: { count: number } }>('/notifications/unread-count'),
  markRead: (id: string) => api.patch(`/notifications/${id}/read`),
  markAllRead: () =>
    api.patch<{ success: boolean; data: { updated: number } }>('/notifications/read-all'),
};

// ΓöÇΓöÇΓöÇ Campaigns ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
export const campaignsApi = {
  list: (params?: Record<string, unknown>) => api.get('/campaigns', { params }),
  get: (id: string) => api.get(`/campaigns/${id}`),
  create: (data: unknown) => api.post('/campaigns', data),
  update: (id: string, data: unknown) => api.put(`/campaigns/${id}`, data),
  delete: (id: string) => api.delete(`/campaigns/${id}`),
  launch: (id: string) => api.post(`/campaigns/${id}/launch`),
  validateWhitelist: (id: string) =>
    api.post<{ success: boolean; valid: boolean; error?: string; code?: string }>(
      `/campaigns/${id}/validate-whitelist`
    ),
  pause: (id: string) => api.post(`/campaigns/${id}/pause`),
  resume: (id: string) => api.post(`/campaigns/${id}/resume`),
  complete: (id: string) => api.post(`/campaigns/${id}/complete`),
  archive: (id: string) => api.post(`/campaigns/${id}/archive`),
  clone: (id: string) => api.post(`/campaigns/${id}/clone`),
  getBatchDeliveryStatus: (campaignId: string) =>
    api.get(`/campaigns/${campaignId}/deliveries/batch-status`),
  batchRetryDeliveries: (campaignId: string, deliveryIds?: string[]) =>
    api.post(`/campaigns/${campaignId}/deliveries/batch-retry`, { deliveryIds }),
  batchCancelDeliveries: (campaignId: string, deliveryIds?: string[]) =>
    api.post(`/campaigns/${campaignId}/deliveries/batch-cancel`, { deliveryIds }),
};

// ΓöÇΓöÇΓöÇ Teachable Moments ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
export const teachableMomentsApi = {
  list: (params?: Record<string, unknown>) => api.get('/teachable-moments', { params }),
  get: (id: string) => api.get(`/teachable-moments/${id}`),
  create: (data: unknown) => api.post('/teachable-moments', data),
  update: (id: string, data: unknown) => api.put(`/teachable-moments/${id}`, data),
  delete: (id: string) => api.delete(`/teachable-moments/${id}`),
};

export type PhishingTemplateStatus = 'draft' | 'enabled' | 'disabled' | 'archived';

export interface PhishingTemplateListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  type?: string;
  category?: string;
  language?: string;
  difficulty?: string;
  enabledOnly?: boolean;
}

export interface PhishingTemplatePayload {
  name: string;
  type: string;
  category: string;
  language: string;
  difficulty: string;
  riskLevel?: string;
  status: PhishingTemplateStatus;
  subject?: string;
  senderName?: string;
  senderEmail?: string;
  previewHtml?: string;
  landingPageHtml?: string;
  translations?: Record<
    string,
    {
      subject?: string;
      previewHtml?: string;
      landingPageHtml?: string;
    }
  >;
  tags?: string[];
  attachment?: { name: string; mimeType: string; sizeBytes: number; contentBase64: string } | null;
  versionNotes?: string;
}

export const phishingTemplatesApi = {
  list: (params?: PhishingTemplateListParams) => api.get('/phishing-templates', { params }),
  meta: () => api.get('/phishing-templates/meta'),
  getBatchInit: (params?: PhishingTemplateListParams) =>
    api.get('/phishing-templates/batch-init', { params }),
  batchUpdateStatus: (templateIds: string[], status: string) =>
    api.patch('/phishing-templates/batch-status', { templateIds, status }),
  get: (id: string) => api.get(`/phishing-templates/${id}`),
  create: (data: PhishingTemplatePayload) => api.post('/phishing-templates', data),
  update: (id: string, data: PhishingTemplatePayload) => api.put(`/phishing-templates/${id}`, data),
  clone: (id: string) => api.post(`/phishing-templates/${id}/clone`),
  setStatus: (id: string, status: PhishingTemplateStatus) =>
    api.patch(`/phishing-templates/${id}/status`, { status }),
  translatePreview: (id: string, data: { targetLanguage: string; sourceLanguage?: string }) =>
    api.post(`/phishing-templates/${id}/translate-preview`, data),
  translateDraft: (data: {
    targetLanguage: string;
    sourceLanguage?: string;
    subject?: string;
    previewHtml?: string;
    landingPageHtml?: string;
  }) => api.post('/phishing-templates/translate-draft', data),
  versions: (id: string) => api.get(`/phishing-templates/${id}/versions`),
  delete: (id: string) => api.delete(`/phishing-templates/${id}`),
};

export const campaignTypesApi = {
  list: (params?: { enabledOnly?: boolean }) => api.get('/campaign-types', { params }),
  getById: (id: string) => api.get(`/campaign-types/${id}`),
  create: (data: any) => api.post('/campaign-types', data),
  update: (id: string, data: any) => api.put(`/campaign-types/${id}`, data),
  setEnabled: (id: string, isEnabled: boolean) => api.patch(`/campaign-types/${id}/status`, { isEnabled }),
  delete: (id: string) => api.delete(`/campaign-types/${id}`),
};


// ΓöÇΓöÇΓöÇ Users ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
export const usersApi = {
  list: (params?: Record<string, unknown>) => api.get('/users', { params }),
  meta: () => api.get('/users/meta'),
  getBatchInit: () => api.get<any>('/users/batch-init'),
  get: (id: string) => api.get(`/users/${id}`),
  reports: (id: string) => api.get(`/users/${id}/reports`),
  getMyReports: () => api.get('/users/me/reports'),
  create: (data: unknown) => api.post('/users', data),
  update: (id: string, data: unknown) => api.put(`/users/${id}`, data),
  getOwnProperties: () => api.get('/users/me/properties'),
  updateOwnProperties: (data: unknown) => api.put('/users/me/properties', data),
  delete: (id: string) => api.delete(`/users/${id}`),
  importCsv: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/users/import', form);
  },
  exportCsv: () => api.get('/users/export', { responseType: 'blob' }),
  lock: (id: string) => api.post(`/users/${id}/lock`),
  unlock: (id: string) => api.post(`/users/${id}/unlock`),
  archive: (id: string) => api.post(`/users/${id}/archive`),
  predictRisk: (userId: string, params?: {
    category?: string;
    difficulty?: string;
    riskLevel?: string;
    sequenceNo?: number;
    reported?: boolean;
  }) => api.get(`/users/${userId}/predict-risk`, { params }),
};

export const userPropertiesApi = {
  list: (params?: Record<string, unknown>) => api.get('/users/properties', { params }),
  create: (data: unknown) => api.post('/users/properties', data),
  update: (id: string, data: unknown) => api.put(`/users/properties/${id}`, data),
  delete: (id: string) => api.delete(`/users/properties/${id}`),
};

export const rolesApi = {
  list: (params?: Record<string, unknown>) => api.get('/roles', { params }),
  getBatchInit: () => api.get<any>('/roles/batch-init'),
  listModules: () => api.get('/roles/modules'),
  get: (id: string) => api.get(`/roles/${id}`),
  create: (data: unknown) => api.post('/roles', data),
  update: (id: string, data: unknown) => api.put(`/roles/${id}`, data),
  delete: (id: string) => api.delete(`/roles/${id}`),
};

// ΓöÇΓöÇΓöÇ Groups ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
export interface TenantRecord {
  id: string;
  name: string;
  slug: string;
  plan: string;
  status: 'trial' | 'active' | 'suspended' | 'archived';
  branding: Record<string, unknown> | null;
  settings: Record<string, unknown> | null;
  createdAt?: string;
  updatedAt?: string;
}

export const tenantsApi = {
  list: () => api.get<{ success: boolean; count: number; data: TenantRecord[] }>('/tenants'),
  get: (id: string) => api.get<{ success: boolean; data: TenantRecord }>(`/tenants/${id}`),
  update: (id: string, data: unknown) =>
    api.put<{ success: boolean; data: TenantRecord }>(`/tenants/${id}`, data),
  delete: (id: string) => api.delete<{ success: boolean; message: string }>(`/tenants/${id}`),
};

export const groupsApi = {
  list: (params?: Record<string, unknown>) => api.get('/groups', { params }),
  getBatchInit: () => api.get<any>('/groups/batch-init'),
  get: (id: string) => api.get(`/groups/${id}`),
  create: (data: unknown) => api.post('/groups', data),
  update: (id: string, data: unknown) => api.put(`/groups/${id}`, data),
  delete: (id: string) => api.delete(`/groups/${id}`),
  members: (id: string) => api.get(`/groups/${id}/members`),
  addMembers: (id: string, userIds: string[]) => api.post(`/groups/${id}/members`, { userIds }),
  removeMembers: (id: string, userIds: string[]) =>
    api.delete(`/groups/${id}/members`, { data: { userIds } }),
  sync: (id: string) => api.post(`/groups/${id}/sync`),
  clone: (id: string) => api.post(`/groups/${id}/clone`),
  // Dynamic Group APIs
  fields: () => api.get('/groups/fields'),
  getRules: (id: string) => api.get(`/groups/${id}/rules`),
  saveRules: (id: string, rules: unknown[]) => api.put(`/groups/${id}/rules`, { rules }),
  preview: (rules: unknown[], page?: number, limit?: number, search?: string) =>
    api.post('/groups/preview', { rules, page, limit, search }),
  // Smart Groups (system-generated)
  smartGroups: (params?: Record<string, unknown>) =>
    api.get('/groups', { params: { ...params, isSystem: 'true' } }),
  // Export members as CSV
  exportMembers: (id: string) => api.get(`/groups/${id}/members`),
  importMembers: (id: string, formData: FormData) =>
    api.post(`/groups/${id}/members/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
};

// ΓöÇΓöÇΓöÇ Training ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
export const trainingApi = {
  listAssignments: (params?: Record<string, unknown>) => api.get('/training', { params }),
  myProgress: () => api.get('/training/my-progress'),
  getBatchInit: (params?: Record<string, unknown>) => api.get('/training/batch-init', { params }),
  previewAudience: (enrollRules: unknown) =>
    api.post('/training/audience/preview', { enrollRules }),
  getAssignment: (id: string) => api.get(`/training/${id}`),
  getAssignmentStats: (id: string) => api.get(`/training/assignments/${id}/stats`),
  createAssignment: (data: unknown) => api.post('/training', data),
  updateAssignment: (id: string, data: unknown) => api.put(`/training/${id}`, data),
  deleteAssignment: (id: string) => api.delete(`/training/${id}`),
  assign: (id: string, data: unknown) => api.post(`/training/${id}/assign`, data),
  bulkUpdate: (data: { assignmentIds: string[]; action: string; dueDate?: string }) =>
    api.post('/training/bulk', data),
  clone: (id: string) => api.post(`/training/${id}/clone`),
  getAssignmentModule: (assignmentId: string, moduleId: string) =>
    api.get(`/training/assignments/${assignmentId}/modules/${moduleId}`),
  getAssignmentModuleProgress: (assignmentId: string, moduleId: string) =>
    api.get(`/training/assignments/${assignmentId}/modules/${moduleId}/progress`),
  trackAssignmentModule: (
    assignmentId: string,
    moduleId: string,
    data: { status?: 'started' | 'completed'; timeSpent?: number; lastPosition?: any; progress?: number }
  ) => api.post(`/training/assignments/${assignmentId}/modules/${moduleId}/track`, data),
  // Records a learner's progress on a single module (content item or assessment)
  // within an assignment, so studying content rolls up to the assignment dashboard.
  trackModuleProgress: (data: {
    assignmentId: string;
    moduleId: string;
    status: 'not_started' | 'in_progress' | 'completed';
    score?: number;
    timeSpent?: number;
    lastPosition?: any;
    progress?: number;
  }) => api.post('/training/progress', data),
};

export const questionBankApi = {
  filters: () => api.get('/question-bank/filters'),
  list: (params?: Record<string, unknown>) => api.get('/question-bank', { params }),
  get: (id: string) => api.get(`/question-bank/${id}`),
  create: (data: unknown) => api.post('/question-bank', data),
  update: (id: string, data: unknown) => api.put(`/question-bank/${id}`, data),
  delete: (id: string) => api.delete(`/question-bank/${id}`),
};

export const assessmentsApi = {
  list: (params?: Record<string, unknown>) => api.get('/assessments', { params }),
  get: (id: string) => api.get(`/assessments/${id}`),
  preview: (id: string) => api.get(`/assessments/${id}/preview`),
  getAssigned: (id: string, params?: { assignmentId?: string }) =>
    api.get(`/assessments/${id}/assigned`, { params }),
  create: (data: unknown) => api.post('/assessments', data),
  update: (id: string, data: unknown) => api.put(`/assessments/${id}`, data),
  delete: (id: string) => api.delete(`/assessments/${id}`),
  attempts: (id: string) => api.get(`/assessments/${id}/attempts`),
  startAttempt: (id: string, data?: { assignmentId?: string }) =>
    api.post(`/assessments/${id}/attempts`, data ?? {}),
  getAttempt: (attemptId: string) => api.get(`/assessments/attempts/${attemptId}`),
  submitAttempt: (attemptId: string, data: unknown) =>
    api.post(`/assessments/attempts/${attemptId}/submit`, data),
  getStats: (id: string, params?: { assignmentId?: string }) =>
    api.get(`/assessments/${id}/stats`, { params }),
};

// ΓöÇΓöÇΓöÇ Content ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
export const contentApi = {
  list: (params?: Record<string, unknown>) => api.get('/content', { params }),
  getLibrarySummary: () => api.get<any>('/content/library-summary'),
  getFilterOptions: () => api.get<any>('/content/filters/options'),
  getUrlInfo: (url: string) => api.get<{ success: boolean; title: string }>('/content/url-info', { params: { url } }),
  getBatchInit: () => api.get<any>('/content/batch-init'),
  search: (params: { q: string; limit?: number }) => api.get<{ success: boolean; data: any[] }>('/content/search', { params }),
  get: (id: string) => api.get(`/content/${id}`),
  create: (data: unknown) => api.post('/content', data),
  update: (id: string, data: unknown) => {
    // Use longer timeout when sending FormData (file uploads)
    const isFileUpload = typeof FormData !== 'undefined' && data instanceof FormData;
    return api.put(`/content/${id}`, data, isFileUpload ? {
      timeout: 300_000, // 5 minutes for file uploads
    } : undefined);
  },
  delete: (id: string) => api.delete(`/content/${id}`),
  clone: (id: string) => api.post(`/content/${id}/clone`),
  track: (id: string, data: { status?: 'started' | 'completed'; lastPosition?: any }) =>
    api.post(`/content/${id}/track`, data),
  getProgress: (id: string) => api.get(`/content/${id}/progress`),
  upload: (data: FormData, onUploadProgress?: (progressEvent: any) => void) =>
    api.post('/content', data, {
      timeout: 300_000, // 5 minutes for file uploads
      onUploadProgress,
    }),
  assign: (id: string, userIds: string[], groupIds: string[]) =>
    api.post(`/content/${id}/assign`, { userIds, groupIds }),
  enroll: (id: string, userIds: string[], groupIds: string[], options?: any) =>
    api.post(`/content/${id}/enroll`, { userIds, groupIds, options }),
  getEnrollments: (id: string) => api.get(`/content/${id}/enrollments`),
  getEnrollmentStats: (id: string) => api.get(`/content/${id}/enrollment-stats`),
  getBulkEnrollmentStats: (contentIds: string[]) =>
    api.post(`/content/bulk-enrollment-stats`, { contentIds }),
  cancelEnrollment: (id: string, userId: string) =>
    api.post(`/content/${id}/enrollments/${userId}/cancel`),
  cancelGroupEnrollment: (id: string, groupId: string) =>
    api.post(`/content/${id}/enrollments/groups/${groupId}/cancel`),
  bulkEnroll: (contentIds: string[], userIds: string[], groupIds: string[], options?: any) =>
    api.post(`/content/bulk-enroll`, { contentIds, userIds, groupIds, options }),
  download: (id: string) => api.get(`/content/${id}/download`, { responseType: 'blob', timeout: 300_000 }),
  scormInspect: (id: string) => api.get(`/content/${id}/scorm-inspect`, { timeout: 120_000 }),
  approve: (id: string) => api.post(`/content/${id}/approve`),
  reject: (id: string, reason: string) => api.post(`/content/${id}/reject`, { reason }),
  auditUnapprovedInAssignments: () => api.get('/content/audit/unapproved-assignments'),
};

// ΓöÇΓöÇΓöÇ Image Library ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
export interface ImageAsset {
  id: string;
  tenantId: string;
  uploadedBy: string;
  filename: string;
  storageKey: string;
  url: string;
  mimeType: string;
  sizeBytes: number;
  width: number | null;
  height: number | null;
  altText: string | null;
  tags: string[];
  category: string | null;
  status: 'active' | 'archived';
  createdAt: string;
  updatedAt: string;
}

export const imageApi = {
  list: (params?: Record<string, unknown>) =>
    api.get<{ success: boolean; data: ImageAsset[]; pagination: any }>('/images', { params }),
  upload: (formData: FormData) =>
    api.post<{ uploaded: any[]; failed: any[] }>('/images/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300_000, // 5 minutes for image uploads
    }),
  update: (id: string, data: { alt_text?: string; tags?: string[]; category?: string }) =>
    api.patch<{ success: boolean; data: ImageAsset }>(`/images/${id}`, data),
  archive: (id: string) =>
    api.delete<{ success: boolean; message: string }>(`/images/${id}`),
  quota: () =>
    api.get<{
      used_bytes: number;
      quota_bytes: number;
      used_mb: number;
      quota_mb: number;
      percentage_used: number;
      remaining_bytes: number;
    }>('/images/quota'),
  categories: () =>
    api.get<{ success: boolean; data: string[] }>('/images/categories'),
  picker: (params?: Record<string, unknown>) =>
    api.get<{ data: any[] }>('/images/picker', { params }),
};

// ΓöÇΓöÇΓöÇ Dashboards & Reporting ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
export const dashboardApi = {
  summary: () => api.get('/dashboard'),
  getBatchInit: (params?: Record<string, unknown>) => api.get('/dashboard/batch-init', { params }),
  getWidgets: () => api.get('/dashboard/widgets'),
  getLayout: () => api.get('/dashboard/layout'),
  saveLayout: (widgets: any[]) => api.put('/dashboard/layout', { widgets }),

  /** Fetch widget data, optionally scoped by active filters. */
  getWidgetData: (key: string, params?: Record<string, string>) =>
    api.get(`/dashboard/widgets/${key}/data`, { params }),

  /** Batch fetch widget data for multiple widgets in parallel */
  getBatchWidgetData: (widgetKeys: string[], filters?: any) =>
    api.post('/dashboard/widgets/batch-data', { widgetKeys, filters }),

  /** Returns org-specific option lists for all filter dimensions. */
  getFilterOptions: () => api.get('/dashboard/filter-options'),

  // ΓöÇΓöÇΓöÇ Widget-level export ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
  /** Download raw underlying data for a single widget. */
  exportWidget: (key: string, format: 'csv' | 'xlsx', params?: Record<string, string>) =>
    api.get(`/dashboard/widgets/${key}/export`, {
      params: { format, ...params },
      responseType: 'blob',
    }),

  // ΓöÇΓöÇΓöÇ Full-dashboard export ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
  /** Request a full-dashboard export. Backend returns a blob or a jobId. */
  exportDashboard: (
    format: 'pdf' | 'xlsx' | 'csv',
    filters?: Record<string, string>,
    widgetKeys?: string[]
  ) =>
    api.post(
      '/dashboard/export',
      { format, filters, widgetKeys },
      { responseType: 'blob' }
    ),

  // ΓöÇΓöÇΓöÇ Filter presets ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
  getPresets: () => api.get('/dashboard/filter-presets'),
  savePreset: (name: string, filters: Record<string, any>) =>
    api.post('/dashboard/filter-presets', { name, filters }),
  deletePreset: (id: string) => api.delete(`/dashboard/filter-presets/${id}`),

  // ΓöÇΓöÇΓöÇ Scheduled exports ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
  listScheduledExports: () => api.get('/dashboard/schedules'),
  createScheduledExport: (config: {
    name: string;
    frequency: 'daily' | 'weekly' | 'monthly';
    format: 'pdf' | 'xlsx' | 'csv';
    recipients: string[];
    filters?: Record<string, any>;
    widgetId?: string | null;
  }) => api.post('/dashboard/schedules', config),
  getRepeatOffenders: (params?: Record<string, unknown>) =>
    api.get('/dashboard/repeat-offenders', { params }),
  deleteScheduledExport: (id: string) => api.delete(`/dashboard/schedules/${id}`),
};

export const reportingApi = {
  list: (params?: Record<string, unknown>) => api.get('/reporting', { params }),
  export: (id: string, params?: Record<string, unknown>) =>
    api.get(`/reporting/${id}/export`, { params, responseType: 'blob' }),
  listScheduled: () => api.get('/reporting/scheduled'),
  createScheduled: (data: unknown) => api.post('/reporting/scheduled', data),
  updateScheduled: (id: string, data: unknown) => api.put(`/reporting/scheduled/${id}`, data),
  deleteScheduled: (id: string) => api.delete(`/reporting/scheduled/${id}`),
};

// ΓöÇΓöÇΓöÇ Analytics ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
export const analyticsApi = {
  // From akshat9fix
  getBatchInit: (params?: Record<string, unknown>) => api.get<any>('/analytics/batch-init', { params }),
  getCampaignMetrics: (params?: Record<string, unknown>) => api.get('/phishing-sim', { params }),
  getCampaignAnalytics: (id: string, params?: Record<string, unknown>) =>
    api.get(`/analytics/campaigns/${id}`, { params }),

  // From shrishti9july
  getReports: () => api.get('/analytics/reports'),
  runReport: (id: string, params: Record<string, unknown>) =>
    api.get(`/analytics/reports/${id}`, { params }),
  exportReport: (id: string, payload: { format: 'csv' | 'excel', filters?: any, sortBy?: string, sortOrder?: string }) =>
    api.post(`/analytics/reports/${id}/export`, payload, {
      responseType: 'blob', // Important for file downloads
    }),
  getDashboard: (params?: Record<string, unknown>) => api.get('/analytics/dashboard', { params }),
  getTrend: (metric: string, period: string) =>
    api.get('/analytics/trends', { params: { metric, period } }),
  getUserTrainingReport: (userId: string) =>
    api.get(`/analytics/reports/training/user/${userId}`),
  exportUserTrainingReport: (userId: string, payload: { format: 'csv' | 'excel' }) =>
    api.post(`/analytics/reports/training/user/${userId}/export`, payload, {
      responseType: 'blob', // Important for file downloads
    }),
};

// ΓöÇΓöÇΓöÇ Whitelist ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
export const whitelistsApi = {
  list: (params?: Record<string, unknown>) => api.get<{ success: boolean; data: any[] }>('/whitelists', { params }),
  get: (id: string) => api.get(`/whitelists/${id}`),
  create: (data: unknown) => api.post('/whitelists', data),
  update: (id: string, data: unknown) => api.put(`/whitelists/${id}`, data),
  delete: (id: string) => api.delete(`/whitelists/${id}`),
};

export const settingsApi = {
  getBatchInit: () => api.get<any>('/settings/batch-init'),
  get: () => api.get<{ success: boolean; data: { settings: Record<string, any> } }>('/settings'),
  updateWorkingHours: (workingHours: unknown) =>
    api.put<{ success: boolean; data: { workingHours: any } }>('/settings/working-hours', workingHours),
};

// ΓöÇΓöÇΓöÇ Awareness Materials ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
export const awarenessMaterialsApi = {
  list: (params?: Record<string, unknown>) => api.get('/awareness-materials', { params }),
  getTypes: () => api.get('/awareness-materials/types'),
  getSummary: () => api.get('/awareness-materials/summary'),
  get: (id: string) => api.get(`/awareness-materials/${id}`),
  create: (data: unknown) => api.post('/awareness-materials', data),
  upload: (formData: FormData) =>
    api.post('/awareness-materials', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300_000, // 5 minutes for file uploads
    }),
  update: (id: string, data: unknown) => api.put(`/awareness-materials/${id}`, data),
  delete: (id: string) => api.delete(`/awareness-materials/${id}`),
  download: (id: string, resolution?: number) =>
    api.get(`/awareness-materials/${id}/download`, {
      params: resolution !== undefined ? { resolution } : undefined,
      responseType: 'blob',
    }),
  // Campaign linking
  getCampaignMaterials: (campaignId: string) =>
    api.get(`/awareness-materials/campaigns/${campaignId}`),
  linkToCampaign: (campaignId: string, contentItemIds: string[]) =>
    api.post(`/awareness-materials/campaigns/${campaignId}/link`, { contentItemIds }),
  unlinkFromCampaign: (campaignId: string, contentItemId: string) =>
    api.delete(`/awareness-materials/campaigns/${campaignId}/link/${contentItemId}`),
};

export const emailAnalyzerApi = {
  list: (params?: Record<string, unknown>) => api.get<{ success: boolean, data: any[] }>('/email-analyzer', { params }),
  upload: (file: File) => {
    const formData = new FormData();
    formData.append('emailFile', file);
    return api.post('/email-analyzer/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  getStatus: (id: string) => api.get(`/email-analyzer/${id}`),
};
